import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import {
  DEFAULT_ADMIN,
  type OnboardingAdminEntry,
  OnboardingAdminEntrySchema,
} from '@innermate/shared';
import { useMutation } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface Props {
  onAdvance: () => void;
}

const EMPTY_ADMIN: OnboardingAdminEntry = { email: '', displayName: '', password: '' };

export function StepAdmin({ onAdvance }: Props) {
  const [admins, setAdmins] = useState<OnboardingAdminEntry[]>([{ ...DEFAULT_ADMIN }]);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (rows: OnboardingAdminEntry[]) => api.onboardingAdmin({ admins: rows }),
    onSuccess: () => onAdvance(),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '알 수 없는 오류');
    },
  });

  const update = (idx: number, patch: Partial<OnboardingAdminEntry>) => {
    setAdmins((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const addRow = () => setAdmins((prev) => [...prev, { ...EMPTY_ADMIN }]);
  const removeRow = (idx: number) => setAdmins((prev) => prev.filter((_, i) => i !== idx));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const next: Record<number, Record<string, string>> = {};
    let hasError = false;
    admins.forEach((a, i) => {
      const parsed = OnboardingAdminEntrySchema.safeParse(a);
      if (!parsed.success) {
        hasError = true;
        const entryErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          entryErrors[String(issue.path[0])] = issue.message;
        }
        next[i] = entryErrors;
      }
    });
    setErrors(next);
    if (hasError) return;
    mutation.mutate(admins);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="rounded-md border border-border bg-surface-muted/60 px-4 py-3 text-sm text-foreground-muted">
        기본 관리자는 <code className="font-mono">admin@innermate.local</code> 로 준비되어 있습니다.
        비밀번호를 그대로 사용하거나 변경할 수 있고, 필요하면 관리자 행을 더 추가할 수 있습니다.
      </div>

      <ul className="space-y-4">
        {admins.map((admin, idx) => {
          const rowErrors = errors[idx] ?? {};
          return (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: 온보딩 admin 폼은 추가/제거만 있고 재정렬 없음
              key={idx}
              className="rounded-lg border border-border bg-surface p-5 space-y-4 relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-foreground-muted">
                  관리자 {idx + 1}
                </span>
                {admins.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-foreground-muted hover:text-foreground inline-flex items-center gap-1 text-xs"
                    aria-label={`관리자 ${idx + 1} 제거`}
                  >
                    <Trash2 className="size-3.5" />
                    제거
                  </button>
                ) : null}
              </div>

              <Field label="이메일" htmlFor={`email-${idx}`} error={rowErrors.email}>
                <Input
                  id={`email-${idx}`}
                  type="email"
                  value={admin.email}
                  onChange={(e) => update(idx, { email: e.target.value })}
                  autoComplete="email"
                />
              </Field>

              <Field label="표시 이름" htmlFor={`displayName-${idx}`} error={rowErrors.displayName}>
                <Input
                  id={`displayName-${idx}`}
                  value={admin.displayName}
                  onChange={(e) => update(idx, { displayName: e.target.value })}
                  autoComplete="name"
                />
              </Field>

              <Field
                label="비밀번호"
                htmlFor={`password-${idx}`}
                hint="8자 이상, 영문과 숫자 모두 포함"
                error={rowErrors.password}
              >
                <Input
                  id={`password-${idx}`}
                  type="password"
                  value={admin.password}
                  onChange={(e) => update(idx, { password: e.target.value })}
                  autoComplete="new-password"
                />
              </Field>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
      >
        <Plus className="size-4" />
        관리자 추가
      </button>

      {serverError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {serverError}
        </div>
      ) : null}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? '계정 생성 중…' : `관리자 ${admins.length}명 만들기`}
        </Button>
      </div>
    </form>
  );
}
